<template>
   <!-- Birds Eye: world map + country table (sample data for now) -->
   <div v-if="!closed" class="card bg-sa-card birds-eye mb-4" :class="{ 'be-fullscreen': fullscreen }">
      <!-- Header -->
      <div class="card-header bg-sa-card-header be-header">
         <span class="be-title"><i class="fa fa-map-marker me-2"></i>Birds Eye</span>
         <div class="be-controls">
            <label class="be-ctrl be-realtime">
               <i class="fa fa-location-arrow me-1"></i><span class="d-none d-sm-inline me-2">Realtime</span>
               <input v-model="realtime" type="checkbox" class="d-none" @change="toggleRealtime" />
               <span class="be-switch" :class="{ on: realtime }">
                  <span class="be-switch-text">{{ realtime ? "YES" : "NO" }}</span>
                  <i class="fa" :class="realtime ? 'fa-check' : 'fa-times'"></i>
               </span>
            </label>
            <button type="button" class="be-ctrl" :title="minimized ? 'Show' : 'Minimize'" @click="toggleMinimize">
               <i class="fa" :class="minimized ? 'fa-plus' : 'fa-minus'"></i>
            </button>
            <button type="button" class="be-ctrl d-none d-md-inline-flex" title="Full screen" @click="toggleFullscreen">
               <i class="fa" :class="fullscreen ? 'fa-compress' : 'fa-expand'"></i>
            </button>
            <button type="button" class="be-ctrl" title="Close" @click="closeWidget">
               <i class="fa fa-times"></i>
            </button>
         </div>
      </div>

      <div v-show="!minimized" class="card-body bg-sa-card-body p-0">
         <!-- Map -->
         <div class="be-map-wrap">
            <div ref="map" class="be-map"></div>
            <div v-if="mapLoading" class="be-map-loading"><i class="fa fa-spinner fa-spin me-2"></i>Loading map…</div>
            <!-- Colour key -->
            <div class="be-legend">
               <span>0</span><span class="be-legend-bar"></span><span>5,000</span>
            </div>
         </div>

         <!-- Country table -->
         <div class="table-responsive">
            <table class="table align-middle mb-0 be-table">
               <thead>
                  <tr>
                     <th>Country</th>
                     <th>Visits</th>
                     <th class="text-center d-none d-sm-table-cell">User Activity</th>
                     <th class="text-center">Online</th>
                     <th class="text-center d-none d-sm-table-cell">Demographic</th>
                     <th></th>
                  </tr>
               </thead>
               <tbody>
                  <tr v-for="c in pageRows" :key="c.code">
                     <td><a href="#" class="be-country" @click.prevent="focusCountry(c.code)">{{ c.name }}</a></td>
                     <td>{{ c.visits.toLocaleString() }}</td>
                     <td class="text-center d-none d-sm-table-cell">
                        <!-- Activity bars (red = drop) -->
                        <svg class="be-spark" viewBox="0 0 52 22">
                           <rect v-for="(bar, i) in sparkBars(c.activity)" :key="i"
                                 :x="bar.x" :y="bar.y" width="2.5" :height="bar.h"
                                 :class="bar.neg ? 'be-spark-neg' : 'be-spark-pos'" />
                        </svg>
                     </td>
                     <td class="text-center">{{ c.online }}</td>
                     <td class="text-center d-none d-sm-table-cell">
                        <!-- Pie: pink = women, steel = men -->
                        <svg class="be-pie" viewBox="0 0 20 20" :title="c.female + '% women'">
                           <circle class="be-pie-base" cx="10" cy="10" r="9" />
                           <circle class="be-pie-slice" cx="10" cy="10" r="4.5" :stroke-dasharray="(c.female * 28.27 / 100) + ' 28.27'" />
                        </svg>
                     </td>
                     <td class="text-end">
                        <button type="button" class="btn btn-sm btn-outline-secondary be-gear" title="Show on the map" @click="focusCountry(c.code)">
                           <i class="fa fa-cog"></i>
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

         <!-- Pages -->
         <div class="be-pages">
            <button type="button" class="be-page" :disabled="page === 1" @click="page--">Previous</button>
            <button v-for="n in totalPages" :key="n" type="button" class="be-page" :class="{ active: n === page }" @click="page = n">{{ n }}</button>
            <button type="button" class="be-page" :disabled="page === totalPages" @click="page++">Next</button>
         </div>
      </div>
   </div>
</template>

<script>
export default {
   name: "BirdsEye",
   data() {
      return {
         closed: false,
         minimized: false,
         fullscreen: false,
         realtime: true,
         realtimeTimer: null,
         map: null,
         mapLoading: true,
         page: 1,
         perPage: 6,
         // Sample data: visits, people online, % women, last 12 activity changes (negative = drop)
         countries: [
            { code: "US", name: "USA", visits: 4977, online: 143, female: 28, activity: [6, 3, 8, 4, 2, 7, 5, 9, 4, 6, 8, 5] },
            { code: "AU", name: "Australia", visits: 4873, online: 247, female: 15, activity: [3, 1, 4, -3, -4, -3, 2, 5, 3, 6, 4, 2] },
            { code: "IN", name: "India", visits: 3671, online: 373, female: 12, activity: [2, 4, 1, 3, 5, 2, 4, 3, 2, 4, 3, 10] },
            { code: "BR", name: "Brazil", visits: 2476, online: 741, female: 32, activity: [4, 5, 4, 5, 3, 5, -5, 4, 3, 5, 4, 2] },
            { code: "TR", name: "Turkey", visits: 1476, online: 123, female: 60, activity: [3, 4, 3, 5, 4, -3, -6, -4, 3, 5, 7, 3] },
            { code: "CA", name: "Canada", visits: 146, online: 23, female: 40, activity: [5, 1, 1, 1, -1, 1, 1, 1, 1, 1, 1, 1] },
            { code: "CN", name: "China", visits: 2985, online: 512, female: 45, activity: [5, 6, 4, 7, 5, 8, 6, 7, 5, 6, 8, 7] },
            { code: "KE", name: "Kenya", visits: 1820, online: 214, female: 48, activity: [2, 3, 5, 4, 6, 5, 7, 6, 8, 7, 9, 8] },
            { code: "NG", name: "Nigeria", visits: 1340, online: 166, female: 44, activity: [3, 2, 4, 3, -2, 3, 4, 5, 3, 4, 5, 4] },
            { code: "ZA", name: "South Africa", visits: 980, online: 98, female: 51, activity: [2, 3, 2, -2, -3, 2, 3, 4, 3, 2, 3, 4] },
            { code: "GB", name: "United Kingdom", visits: 2210, online: 187, female: 47, activity: [4, 5, 4, 6, 5, 4, 5, 6, 4, 5, 6, 5] },
            { code: "DE", name: "Germany", visits: 1990, online: 156, female: 43, activity: [3, 4, 5, 4, 3, -2, 3, 4, 5, 4, 3, 4] },
            { code: "FR", name: "France", visits: 1650, online: 132, female: 49, activity: [4, 3, 4, 5, 4, 3, 4, -3, 4, 5, 4, 3] },
            { code: "RU", name: "Russia", visits: 760, online: 54, female: 38, activity: [2, 1, 2, 3, 2, -2, -3, 2, 1, 2, 3, 2] },
            { code: "JP", name: "Japan", visits: 1210, online: 101, female: 36, activity: [3, 4, 3, 4, 5, 4, 3, 4, 5, 4, 5, 6] },
         ],
      };
   },
   computed: {
      totalPages() {
         return Math.max(1, Math.ceil(this.countries.length / this.perPage));
      },
      pageRows() {
         const start = (this.page - 1) * this.perPage;
         return this.countries.slice(start, start + this.perPage);
      },
   },
   async mounted() {
      await this.loadMap();
      if (this.realtime) this.toggleRealtime();
   },
   beforeUnmount() {
      clearInterval(this.realtimeTimer);
      if (this.map && typeof this.map.destroy === "function") this.map.destroy();
   },
   methods: {
      // Map library loads only now (after the page shows), so opening the dashboard stays fast
      async loadMap() {
         try {
            const { default: jsVectorMap } = await import("jsvectormap");
            await import("jsvectormap/dist/maps/world.js");
            if (!this.$refs.map) return;
            this.map = new jsVectorMap({
               selector: this.$refs.map,
               map: "world",
               backgroundColor: "transparent",
               zoomButtons: true,
               zoomOnScroll: false,
               regionStyle: {
                  initial: { fill: "rgba(128,128,128,0.35)", stroke: "none" },
                  hover: { fillOpacity: 0.8, cursor: "pointer" },
               },
               series: {
                  regions: [{
                     attribute: "fill",
                     // 5 shades: more visits = darker
                     scale: { l1: "#c5d3dc", l2: "#a7bfcc", l3: "#86a6b6", l4: "#5f8798", l5: "#3f6d80" },
                     values: this.mapValues(),
                  }],
               },
               onRegionTooltipShow: (event, tooltip, code) => {
                  const c = this.countries.find((x) => x.code === code);
                  if (c) tooltip.text(`${tooltip.text()}: ${c.visits.toLocaleString()} visits`);
               },
            });
         } catch (e) {
            console.error("Birds Eye map failed to load:", e);
         } finally {
            this.mapLoading = false;
         }
      },
      // Which shade each country gets
      shade(visits) {
         if (visits < 500) return "l1";
         if (visits < 1500) return "l2";
         if (visits < 2500) return "l3";
         if (visits < 4000) return "l4";
         return "l5";
      },
      mapValues() {
         const values = {};
         this.countries.forEach((c) => { values[c.code] = this.shade(c.visits); });
         return values;
      },
      // Activity bars: positive go up from the middle, negative (red) go down
      sparkBars(values) {
         const max = Math.max(...values.map((v) => Math.abs(v)), 1);
         return values.map((v, i) => {
            const h = Math.max(1, (Math.abs(v) / max) * 10);
            return { x: i * 4.2 + 1, y: v < 0 ? 11 : 11 - h, h, neg: v < 0 };
         });
      },
      // ⚙ or country name: zoom the map to that country
      focusCountry(code) {
         if (this.map && typeof this.map.setFocus === "function") {
            this.map.setFocus({ region: code, animate: true });
         }
      },
      // Realtime YES: numbers move a little every 3 seconds (sample)
      toggleRealtime() {
         clearInterval(this.realtimeTimer);
         if (!this.realtime) return;
         this.realtimeTimer = setInterval(() => {
            let shadeChanged = false;
            this.countries.forEach((c) => {
               const before = this.shade(c.visits);
               c.visits += Math.floor(Math.random() * 4);
               c.online = Math.max(0, c.online + Math.floor(Math.random() * 11) - 5);
               c.activity = [...c.activity.slice(1), Math.floor(Math.random() * 12) - 3];
               if (this.shade(c.visits) !== before) shadeChanged = true;
            });
            // Re-colour the map only when a country moved to another shade
            if (shadeChanged && this.map && this.map.series && this.map.series.regions[0]) {
               try { this.map.series.regions[0].setValues(this.mapValues()); } catch (e) { /* keep the old colours */ }
            }
         }, 3000);
      },
      toggleMinimize() {
         this.minimized = !this.minimized;
         if (!this.minimized) this.$nextTick(() => window.dispatchEvent(new Event("resize")));
      },
      toggleFullscreen() {
         this.fullscreen = !this.fullscreen;
         // Let the map fit its new size
         this.$nextTick(() => window.dispatchEvent(new Event("resize")));
      },
      closeWidget() {
         clearInterval(this.realtimeTimer);
         this.closed = true;
      },
   },
};
</script>

<style scoped>
/* ---------- Header ---------- */
.be-header {
   display: flex;
   align-items: stretch;
   justify-content: space-between;
   padding-top: 0;
   padding-bottom: 0;
}
.be-title {
   display: flex;
   align-items: center;
   padding: 0.6rem 0;
}
.be-controls {
   display: flex;
   margin-right: calc(-1 * var(--bs-card-cap-padding-x, 1rem));
}
.be-ctrl {
   display: inline-flex;
   align-items: center;
   padding: 0 0.85rem;
   background: transparent;
   border: 0;
   border-left: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3));
   color: inherit;
   cursor: pointer;
   margin: 0;
}
.be-ctrl:hover {
   background: var(--table-row-bg-hover, rgba(128, 128, 128, 0.12));
}
/* YES / NO switch */
.be-switch {
   display: inline-flex;
   align-items: center;
   gap: 0.35rem;
   padding: 0.1rem 0.15rem 0.1rem 0.55rem;
   border-radius: 1rem;
   background: rgba(128, 128, 128, 0.45);
   color: #fff;
   font-size: 0.7rem;
   font-weight: 700;
}
.be-switch.on {
   background: var(--sa-accent, #3276b1);
}
.be-switch i {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 1.1rem;
   height: 1.1rem;
   border-radius: 50%;
   background: #fff;
   color: #6c757d;
   font-size: 0.6rem;
}
.be-switch.on i {
   color: var(--sa-accent, #3276b1);
}

/* ---------- Map ---------- */
.be-map-wrap {
   position: relative;
   padding: 0.5rem 0.5rem 0;
}
.be-map {
   height: 300px;
}
.be-map-loading {
   position: absolute;
   inset: 0;
   display: flex;
   align-items: center;
   justify-content: center;
   opacity: 0.7;
}
.be-legend {
   display: flex;
   align-items: center;
   gap: 0.4rem;
   padding: 0.4rem 0.25rem 0.6rem;
   font-size: 0.8rem;
   font-weight: 600;
}
.be-legend-bar {
   width: 8rem;
   height: 0.4rem;
   background: linear-gradient(to right, #c5d3dc, #3f6d80);
}
/* The map library's own parts (zoom buttons, tooltip) */
.be-map :deep(.jvm-zoom-btn) {
   position: absolute;
   left: 0.5rem;
   width: 1.6rem;
   height: 1.6rem;
   display: flex;
   align-items: center;
   justify-content: center;
   background: var(--sa-custom-dropdown-bg, #fff);
   color: inherit;
   border: 1px solid var(--sa-border, rgba(128, 128, 128, 0.5));
   border-radius: 2px;
   font-weight: 700;
   cursor: pointer;
   user-select: none;
   z-index: 2;
}
.be-map :deep(.jvm-zoomin) { top: 0.5rem; }
.be-map :deep(.jvm-zoomout) { top: 2.4rem; }
.be-map :deep(.jvm-container) { position: relative; width: 100%; height: 100%; overflow: hidden; }

/* ---------- Table ---------- */
.be-table th {
   font-size: 0.85rem;
   white-space: nowrap;
}
.be-country {
   color: var(--bs-link-color);
   text-decoration: none;
}
.be-spark {
   width: 52px;
   height: 22px;
}
.be-spark-pos { fill: #57889c; }
.be-spark-neg { fill: #a90329; }
.be-pie {
   width: 22px;
   height: 22px;
   transform: rotate(-90deg);
}
.be-pie-base { fill: #57889c; }
.be-pie-slice {
   fill: none;
   stroke: #e36fb5;
   stroke-width: 9;
}
.be-gear {
   padding: 0.15rem 0.45rem;
}

/* ---------- Pages ---------- */
.be-pages {
   display: flex;
   padding: 0.6rem 0.75rem;
}
.be-page {
   padding: 0.3rem 0.7rem;
   background: transparent;
   color: inherit;
   border: 1px solid var(--sa-border, rgba(128, 128, 128, 0.35));
   margin-left: -1px;
}
.be-page.active {
   background: var(--sa-accent, #3276b1);
   border-color: var(--sa-accent, #3276b1);
   color: #fff;
}
.be-page:disabled {
   opacity: 0.5;
}

/* ---------- Full screen ---------- */
.be-fullscreen {
   position: fixed;
   inset: 0;
   z-index: 1050;
   margin: 0 !important;
   overflow: auto;
}
.be-fullscreen .be-map {
   height: 60vh;
}
</style>

<!-- Map tooltip lives at the end of the page, so it needs a normal (not scoped) style -->
<style>
.jvm-tooltip {
   position: absolute;
   display: none;
   padding: 0.3rem 0.5rem;
   border-radius: 3px;
   background: rgba(0, 0, 0, 0.8);
   color: #fff;
   font-size: 0.8rem;
   white-space: nowrap;
   pointer-events: none;
   z-index: 2000;
}
.jvm-tooltip.active {
   display: block;
}
</style>